import { UserType } from 'src/type/user';

import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import axios, { endpoints } from 'src/utils/axios';
import InfoGrid from 'src/components/ui-kit/info-grid';
import { fNumber } from 'src/utils/format-number';
import useCurrentDateTime from 'src/hooks/use-current-date-time';

const defaultValues = {
  creditAmount: 0,
};

const Schema = Yup.object().shape({
  creditAmount: Yup.number()
    .typeError('จำนวนเงินต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุจำนวนเงิน')
    .moreThan(-1, 'จำนวนเงินขั้นต่ำ 0'),
});

type Props = {
  open: boolean;
  onClose: VoidFunction;
  user: UserType;
  handleReload: VoidFunction;
};

export default function WithdrawCreditCreateDialog({ open, onClose, user, handleReload }: Props) {
  const currentDateTime = useCurrentDateTime();

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    try {
      const body = {
        uid: user.uid,
        creditAmount: Number(values.creditAmount),
        currentDateTime,
      };

      await axios.post(endpoints.transactions.withdrawCredit.create, body);
      handleReload();
      beforeClose();
      enqueueSnackbar('สร้างรายการสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const getInfo = () => {
    if (!user) return [];

    return [
      { label: 'ผู้ใช้งาน', value: `${user?.firstName} ${user?.lastName}` },
      {
        label: 'ยอดเงินคงเหลือ',
        value: `${fNumber(user?.userWallet?.credit || 0)} THB`,
      },
      {
        label: 'ชื่อธนาคาร',
        value: user?.userWallet?.bank?.name || '-',
      },
      {
        label: 'ชื่อบัญชี',
        value: user?.userWallet?.accountName || '-',
      },
      {
        label: 'เลขที่บัญชี',
        value: user?.userWallet?.accountNumber || '-',
      },
    ];
  };

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>สร้างรายการถอนเงิน</DialogTitle>

        <DialogContent>
          <InfoGrid infos={getInfo()} />

          <Stack spacing={3} py={1}>
            <RHFTextField
              name="creditAmount"
              type="number"
              label="จำนวนเงิน"
              placeholder="0"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography fontWeight="bold" color="success.main">
                      THB
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
